import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useFetchProtectedData } from "./hooks/useFetchProtectedData";
import Profile from "./components/Profile/Profile";
import Login from "./components/Login/Login";
import Callback from "./components/Callback/Callback";
import Dashboard from "./pages/Dashboard/Dashboard";
import Section from "./pages/Section/Section";
import QuestionnairePage from "./pages/QuestionnairePage/QuestionnairePage";
import Header from "./components/Header/Header";
import ThankYouPage from "./pages/ThankYouPage/ThankYouPage";
import BackToTop from "./components/BackToTop/BackToTop";
import Loading from "./components/Loading/Loading";
import Footer from "./components/Footer/Footer";
import { sendAnswers } from "./services/api";
import { sendTrainingEmail } from "./services/platformApi";
import "./App.scss";
import { useAppContext } from "./context/AppContext";
import AdminRoute from "./components/routes/AdminRoute";
import ProductPage from "./pages/ProductPage/ProductPage";
import FaqPage from "./pages/FaqPage/FaqPage";
import Admin from "./pages/Admin/Admin";
import DevTablesPage from "./pages/DevTablesPage/DevTablesPage";
import { isProductKey } from "./config/products";
import { CollectedAnswer, OnAnswersCollected } from "./types/training";
import { QuestionnaireDefinition, SectionRouteData } from "./types/content";
import {
  clearCollectedAnswers,
  getCollectedAnswers,
  getCompletedSections,
  setCollectedAnswers as persistCollectedAnswers,
  setCompletedSections as persistCompletedSections,
} from "./utils/trainingStorage";
import { isAdminUser } from "./utils/auth";
import { useUserProducts } from "./hooks/useUserProducts";

// Component to handle URL changes and update context
const RouteObserver = () => {
  const location = useLocation();
  const { setSelectedProduct } = useAppContext();

  useEffect(() => {
    const [, productKey] = location.pathname.split("/");

    if (productKey && isProductKey(productKey)) {
      setSelectedProduct(productKey);
    }
  }, [location.pathname, setSelectedProduct]);

  return null;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const isAdmin = isAdminUser(user);
  const [collectedAnswers, setCollectedAnswers] = useState<CollectedAnswer[]>([]);
  const [, setCompletedSections] = useState<number>(0);
  const { material, questionnaire_material, selectedProduct } = useAppContext();
  const { products, productsLoading } = useUserProducts(user);

  useEffect(() => {
    setCollectedAnswers(getCollectedAnswers(selectedProduct));
    setCompletedSections(getCompletedSections(selectedProduct));
  }, [selectedProduct]);

  useFetchProtectedData();

  if (isLoading || productsLoading) {
    return <Loading />;
  }

  const sections: SectionRouteData[] = material.topics.map((item) => ({
    topic_image: item.topic_image,
    title: item.title,
    type: item.type,
    content: item.content,
    sectionId: item.sectionId,
    nextRoute: "/questionnaire" + item.sectionId,
  }));

  const handleAnswersCollected: OnAnswersCollected = async (answers) => {
    const updatedAnswers = [...collectedAnswers, ...answers];

    // Filter out duplicate answers based on the question field
    const uniqueAnswers = updatedAnswers.filter(
      (answer, index, self) => index === self.findIndex((a) => a.question === answer.question)
    );

    setCollectedAnswers(uniqueAnswers);
    persistCollectedAnswers(selectedProduct, uniqueAnswers);

    const completedSections = getCompletedSections(selectedProduct);
    if (completedSections < sections.length) {
      persistCompletedSections(selectedProduct, completedSections + 1);
      setCompletedSections(completedSections + 1);
    }

    if (completedSections + 1 >= sections.length && selectedProduct === "firstaid") {
      await sendAnswers({
        user: { id: user?.sub || "", email: user?.email || "" },
        answers: uniqueAnswers,
        selectedProduct,
      });

      await sendTrainingEmail({
        user,
        answers: uniqueAnswers,
        selectedProduct,
      });

      clearCollectedAnswers(selectedProduct);
    }
  };

  return (
    <>
      <RouteObserver />
      <Header />
      <BackToTop />
      <div className="content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <AdminRoute isAuthenticated={isAuthenticated} isAdmin={isAdmin}>
                <Admin />
              </AdminRoute>
            }
          />
          <Route
            path="/dev"
            element={
              <AdminRoute isAuthenticated={isAuthenticated} isAdmin={isAdmin}>
                <DevTablesPage />
              </AdminRoute>
            }
          />
          <Route path="/callback" element={<Callback />} />
          <Route
            path="/profile"
            element={isAuthenticated ? <Profile /> : <Navigate to="/login" />}
          />
          <Route
            path={`/${selectedProduct}/dashboard`}
            element={isAuthenticated ? <Dashboard sections={sections} /> : <Navigate to="/login" />}
          />
          <Route path={`/${selectedProduct}/thankyoupage`} element={<ThankYouPage />} />
          <Route path={`/faq`} element={<FaqPage />} />
          {sections.map((section: SectionRouteData, index: number) => (
            <Route
              key={section.title}
              path={`/${selectedProduct}/section${index + 1}`}
              element={<Section {...section} onAnswersCollected={handleAnswersCollected} />}
            />
          ))}
          {questionnaire_material?.content
            .filter(
              (questionnaire: QuestionnaireDefinition) =>
                questionnaire.questions && questionnaire.questions.length > 0
            )
            .map((questionnaire: QuestionnaireDefinition, index: number) => (
              <Route
                key={questionnaire.title}
                path={`/${selectedProduct}/questionnaire${questionnaire.sectionId}`}
                element={
                  <QuestionnairePage
                    {...questionnaire}
                    onAnswersCollected={handleAnswersCollected}
                  />
                }
              />
            ))}
          <Route path="/product" element={<ProductPage products={products} />} />
          <Route
            path="/"
            element={
              isAuthenticated ? <ProductPage products={products} /> : <Navigate to="/login" />
            }
          />
        </Routes>
      </div>
      <Footer />
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <div className="app-container">
        <AppContent />
      </div>
    </Router>
  );
};

export default App;
